import i18next, { type Resource } from 'i18next'
import Backend from 'i18next-http-backend'
import { initReactI18next, useTranslation as useTranslationOrig, Trans } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

export interface I18NextInitOptions {
  useBackend?: boolean
  defaultNs?: string
}

/**
 * Initialize i18n locales.
 * @param resources - The localization resources to be used.
 * @param defaultLng - The default language to be used.
 * @returns The initialized i18n instance.
 */
export const i18NextInit = (resources: Resource, defaultLng?: string, options?: I18NextInitOptions): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    // eslint-disable-next-line import/no-named-as-default-member -- use method should be called from i18next instance
    void i18next
      .use(LanguageDetector)
      .use(initReactI18next)
      .init(
        {
          lng: defaultLng,
          fallbackLng: defaultLng ?? 'en',
          interpolation: {
            escapeValue: false,
          },
          resources,
          defaultNS: options?.defaultNs ?? 'common',
        },
        (err) => {
          if (err) {
            const error = err as Error
            reject(new Error(error.message || 'Initialization failed'))
          } else {
            resolve()
          }
        },
      )
    if (options?.useBackend) {
      // eslint-disable-next-line import/no-named-as-default-member -- use method should be called from i18next instance
      i18next.use(Backend)
    }
  })

/**
 * Change i18n language
 * @param lng - The language to be changed to
 */
export const changeLanguage = (lng: string): void => {
  // eslint-disable-next-line import/no-named-as-default-member -- changeLanguage method should be called from i18next instance
  void i18next.changeLanguage(lng)
}

/**
 * Change i18n language
 * @param lng - The language to be changed to
 * @param ns - The namespace to be changed to
 * @param resources - The resources to be added
 */
export const addResourceBundle = (lng: string, ns: string, resources: Resource): void => {
  try {
    void i18next.addResourceBundle(lng, ns, resources)
  } catch (error) {
    // console.error(error)
  }
}

/**
 * Get current i18n language
 */
export const getCurrentLanguage = (): string => {
  return i18next.language
}

export const useTranslation = useTranslationOrig
export type { Resource, ResourceLanguage, TFunction } from 'i18next'

export { i18next, Trans }
